import {
  makeRng,
  type MockSectionCustomerInput,
} from './mockSectionCustomer.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { formatWarehouseMeasurementPeriodFullMonthLabel } from './reportMeasurementPeriod.util';
import { resolveUsStateLabel } from './usStateCode.constants';

/** Closing paragraph below the Looking Ahead lead copy. */
export const LOOKING_AHEAD_CLOSING_PARAGRAPH =
  'Every data point in this report represents a real person whose life got a little easier because someone from their community showed up for them.';

/** Section overline for Looking Ahead. */
export const LOOKING_AHEAD_OVERLINE = 'Looking Ahead';

/** Section headline for Looking Ahead. */
export const LOOKING_AHEAD_HEADLINE = 'The momentum is here.';

/**
 * Coerces a warehouse or snapshot engagement rate to a whole-number percent.
 * @param value - Rate as percent (51) or decimal (0.51).
 * @returns Rounded percent or null when invalid.
 */
export const coerceEngagementRatePct = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed =
    typeof value === 'number' ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(parsed)) {
    return null;
  }
  if (parsed > 0 && parsed <= 1) {
    return Math.round(parsed * 100);
  }
  return Math.round(parsed);
};

/**
 * Deterministic mock engagement rate for draft previews without warehouse data.
 * @param customer - Report customer context.
 * @param reportingPeriod - Stored reporting period label.
 * @returns Whole-number engagement rate percent.
 */
export const mockLookingAheadEngagementRatePct = (
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): number => {
  const rng = makeRng(customer.id, reportingPeriod, 'looking-ahead');
  return Math.round((0.45 + rng() * 0.1) * 100);
};

/**
 * Resolves the engagement rate percent for Looking Ahead copy.
 * @param customer - Report customer context.
 * @param reportingPeriod - Stored reporting period label.
 * @param warehouseRate - Optional warehouse-backed rate.
 * @returns Whole-number engagement rate percent.
 */
export const resolveLookingAheadEngagementRatePct = (
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
  warehouseRate?: number | null,
): number => {
  const fromWarehouse = coerceEngagementRatePct(warehouseRate);
  if (fromWarehouse !== null) {
    return fromWarehouse;
  }
  return mockLookingAheadEngagementRatePct(customer, reportingPeriod);
};

/**
 * Builds the lead paragraph with state and engagement rate personalization.
 * @param stateName - US state display label.
 * @param engagementRatePct - Whole-number engagement rate percent.
 * @returns Studio lead paragraph for Looking Ahead.
 */
export const buildLookingAheadLeadParagraph = (
  stateName: string,
  engagementRatePct: number,
): string =>
  `Members in ${stateName} are getting connected to resources that address the root causes of their health challenges — from utility bills to depression to first PCP visits. The engagement rate is holding steady at ${engagementRatePct}%, the guide network is deepening, and the clinical outcomes are moving in the right direction.`;

/**
 * Builds the prepared-by footer line for Looking Ahead.
 * @param customerName - Report customer display name.
 * @param reportingPeriod - Human-readable reporting period label.
 * @returns Footer pill text.
 */
export const buildLookingAheadPreparedBy = (
  customerName: string,
  reportingPeriod: string,
): string =>
  `Report prepared by Reema Health · ${customerName} · ${formatWarehouseMeasurementPeriodFullMonthLabel(reportingPeriod)}`;

export interface BuildLookingAheadSnapshotParams {
  customer: MockSectionCustomerInput;
  reportingPeriod: string;
  engagementRatePct?: number | null;
  engagementRateSource?: 'warehouse' | 'mock';
}

/**
 * Builds the looking-ahead section snapshot with customer and warehouse context.
 * @param params - Customer, reporting period, and optional warehouse engagement rate.
 * @returns Snapshot JSON for preview, publish, and refresh.
 */
export const buildLookingAheadSnapshot = ({
  customer,
  reportingPeriod,
  engagementRatePct = null,
  engagementRateSource = 'mock',
}: BuildLookingAheadSnapshotParams): Record<string, unknown> => {
  const stateName = resolveUsStateLabel(customer.usStateCode);
  const resolvedRate = resolveLookingAheadEngagementRatePct(
    customer,
    reportingPeriod,
    engagementRateSource === 'warehouse' ? engagementRatePct : null,
  );
  const fieldSources: ReportSectionFieldSources = {
    engagementRatePct: engagementRateSource,
    leadParagraph: engagementRateSource,
    stateName: 'mock',
    preparedBy: 'mock',
    reportingPeriod: 'mock',
  };

  return {
    overline: LOOKING_AHEAD_OVERLINE,
    headline: LOOKING_AHEAD_HEADLINE,
    leadParagraph: buildLookingAheadLeadParagraph(stateName, resolvedRate),
    closingParagraph: LOOKING_AHEAD_CLOSING_PARAGRAPH,
    preparedBy: buildLookingAheadPreparedBy(customer.name, reportingPeriod),
    stateName,
    engagementRatePct: resolvedRate,
    reportingPeriod,
    fieldSources,
  };
};
